import { useMutation, useQueryClient } from '@tanstack/react-query';
import dataService from 'services/dataService';

import { showResult } from '../utils';
import { getUnregisteredConversationNotesKey } from './useUnregisteredConversationNotes';

interface Props {
  onSuccess: () => void;
}

const createUnregisteredConversationNotesFn = async ({
  conversationId,
  note,
}: {
  conversationId: number;
  note: string;
}) => {
  const API_UNREGISTERED_CONVERSATION_NOTES = `core-2/conversations/${conversationId}/notes`;
  const { data, error } = await dataService.createOne(
    API_UNREGISTERED_CONVERSATION_NOTES,
    {
      note,
    },
  );

  if (error) {
    showResult(error);
    throw new Error(error);
  }

  return data;
};

export const useCreateUnregisteredConversationNotes = ({
  onSuccess,
}: Props) => {
  const queryClient = useQueryClient();
  const { mutate, isLoading, ...other } = useMutation({
    mutationFn: createUnregisteredConversationNotesFn,
    onSuccess(data, variables) {
      const unregisteredConversationNotesKey =
        getUnregisteredConversationNotesKey(variables.conversationId);
      onSuccess();
      queryClient.invalidateQueries({
        queryKey: unregisteredConversationNotesKey,
      });
    },
  });

  return {
    createUnregisteredConversationNote: mutate,
    createUnregisteredConversationNoteLoading: isLoading,
    ...other,
  };
};
